import React, { useEffect } from 'react';
import { Support } from '../../hooks/Support';
import Hls from 'hls.js';

declare global {
  interface HTMLElement {
    webkitSetPresentationMode?: (type: string) => Promise<void>;
    requestPictureInPicture?: () => Promise<void>;
    exitPictureInPicture?: () => Promise<void>;
  }
}

export type PipProps = {
  enabled: boolean;
  hls: Hls;
  code: string;
  supported: Support;
};

const Pip = ({ enabled, hls, code, supported }: PipProps) => {
  const [isPip, setIsPip] = React.useState<boolean>(false);

  const pictureInPicture = () => {
    const toggle = !isPip;

    if (!hls.media) {
      return;
    }

    // Toggle based on current state
    // Safari
    if (hls.media.webkitSetPresentationMode) {
      hls.media.webkitSetPresentationMode(toggle ? 'picture-in-picture' : 'inline');
      return;
    }

    // Chrome
    if (hls.media.requestPictureInPicture) {
      if (toggle) {
        hls.media.requestPictureInPicture();
      } else if (!toggle) {
        document.exitPictureInPicture();
      }
    }
  };

  const onEnterPictureInPicture = () => {
    setIsPip(true);
    hls?.media?.dispatchEvent(new CustomEvent('enterPip'));
  };

  const onLeavePictureInPicture = () => {
    setIsPip(false);
    hls?.media?.dispatchEvent(new CustomEvent('leavePip'));
  };

  useEffect(() => {
    if (!hls || !hls.media) {
      return;
    }

    hls?.media?.addEventListener("enterpictureinpicture", onEnterPictureInPicture);
    hls?.media?.addEventListener("leavepictureinpicture", onLeavePictureInPicture);

    return () => {
      if (!hls || !hls?.media) {
        return;
      }

      hls?.media?.removeEventListener("enterpictureinpicture", onEnterPictureInPicture);
      hls?.media?.removeEventListener("leavepictureinpicture", onLeavePictureInPicture);
    };
  }, [hls, hls?.media]);

  return enabled ? (
    <div className={`smartplayer__pip ${isPip ? 'smartplayer__pip--active' : 'smartplayer__pip--inactive'}`} onClick={() => pictureInPicture()}>
      <button className="smartplayer__bar-button" type="button" disabled={!supported.pip}>
        <div className="icon icon--pip-active">
          <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.6667 2.66667H3.33341V9.33333H12.6667V2.66667ZM15.3334 10.6667V1.32C15.3334 0.586667 14.7334 0 14.0001 0H2.00008C1.26675 0 0.666748 0.586667 0.666748 1.32V10.6667C0.666748 11.4 1.26675 12 2.00008 12H14.0001C14.7334 12 15.3334 11.4 15.3334 10.6667ZM14.0001 10.68H2.00008V1.31333H14.0001V10.68Z" fill="currentColor"/>
          </svg>
        </div>
        <div className="icon icon--pip-inactive">
          <svg width="16" height="14" viewBox="0 0 16 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.0909 6.27273H7.27273V10.6364H13.0909V6.27273ZM16 12.0909V1.89454C16 1.09454 15.3455 0.454544 14.5455 0.454544H1.45455C0.654545 0.454544 0 1.09454 0 1.89454V12.0909C0 12.8909 0.654545 13.5455 1.45455 13.5455H14.5455C15.3455 13.5455 16 12.8909 16 12.0909ZM14.5455 12.1055H1.45455V1.88727H14.5455V12.1055Z" fill="currentColor"/>
          </svg>
        </div>
      </button>
    </div>
  ) : null;
};

export default Pip;
